import React, { useCallback, useEffect, useState } from 'react';
import styles from 'components/existingPatient/page.module.scss';
import { StateType } from 'reducer';
import { usePersistExistingPatientReducer } from 'reducer/existingUser';
import OutlineButton from 'components/common/outlineButton';
import {
  ConditionalOptionsExistingType,
  OriginalSymptomsReactionType,
} from 'types/existingPatients';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import MetaTag from 'components/meta';
import { forEach } from 'lodash';
import Label from 'components/common/label';
import Button from 'components/common/button';
import clsx from 'clsx';

enum ReactionName {
  Improving = 'Improving',
  Worsening = 'Worsening',
  Unchanged = 'Unchanged',
}

type Props = {
  options: ConditionalOptionsExistingType[];
  nextPage: number;
  actionPage: string;
  multiple?: boolean;
  isFilter?: boolean;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function SymptomsPage({
  options,
  nextPage,
  nextOrSubmitButton,
  actionPage,
  multiple = true,
  isFilter = false,
}: Props) {
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState<string | null>('');
  const [questionItems, setQuestionItems] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedSymptoms, setSelectedSymptoms] = useState<
    OriginalSymptomsReactionType[]
  >([]);

  const [error, setError] = useState<string>('');
  const symptomsState = useSelector((state: RootState) => state.symptoms);

  const [state, dispatch] = usePersistExistingPatientReducer();

  useEffect(() => {
    if (isFilter) {
      const getQuestion = options.find(
        (obj: ConditionalOptionsExistingType) => obj.page === actionPage,
      );

      const filterQuestions = symptomsState.attributes.filter(
        (item) => item.indexOf('Other') === -1,
      );

      // if not exits symptoms
      if (!filterQuestions.length) {
        nextOrSubmitButton(nextPage);
      }

      setQuestionItems(filterQuestions);
      setTitle(getQuestion?.title ?? '');
      setSubTitle(getQuestion?.subTitle ?? '');
    }
  }, []);

  useEffect(() => {
    if (state) {
      const data = state.find((object: StateType) => object.name === actionPage);
      if (data) {
        const option: string[] = [];
        forEach(data.value, (i) => {
          option.push(i.name);
        });
        setSelectedItems(option);
        setSelectedSymptoms(data.value);
      }
    }
  }, []);

  const onSelectOptions = useCallback((item: string) => {
    if (multiple) {
      setSelectedItems((old) => {
        if (old.find((value) => value === item)) {
          return old.filter((data) => data !== item);
        } return [...old, item];
      });
    } else {
      setSelectedItems([item]);
    }

    setError('');
  }, []);

  const clickNextOrSubmit = async (nextPage: number) => {
    if (selectedItems.length === 0) {
      setError('Please select at least one option.');
      return;
    }

    // eslint-disable-next-line consistent-return
    forEach(selectedItems, (item) => {
      const findSubItem = selectedSymptoms.find((obj) => obj.name === item);

      if (!findSubItem || findSubItem?.value === '') {
        setError(
          "Please select 'Is it getting improving or worsening' option!",
        );
        return true;
      }
    });

    const dispatchPayload = {
      type: actionPage,
      name: title,
      value: selectedSymptoms,
    };

    await dispatch(dispatchPayload);
    nextOrSubmitButton(nextPage);
  };

  const onClickSymptomsStatus = (name: string, value: string) => {
    setSelectedSymptoms((prev: OriginalSymptomsReactionType[]) => {
      const ifExisting = prev.find((e) => e.name === name);
      if (ifExisting) {
        const filterByRemove = prev.filter((e) => e.name !== name);
        return [...filterByRemove, { name, value }];
      }
      return [...prev, { name, value }];
    });
    setError('');
  };

  return (
    <>
      <MetaTag title={title} />
      <h2 className={`marginTopZero ${styles.titleText}`}>{title}</h2>
      {subTitle && <p className={styles.titleTextSubText}>{subTitle}</p>}
      {error && <p className="error-message">{error}</p>}

      <div>
        {questionItems.map((option) => {
          const selectedClass = selectedItems.find(
            (item) => item === option,
          );
          const selectedReaction = selectedSymptoms.find(
            (item) => item.name === option,
          );
          return (
            <div key={option}>
              <p
                className={clsx('option', { 'selected-option': selectedClass })}
                onKeyUp={() => {}}
                onClick={() => onSelectOptions(option)}
              >
                {option}
              </p>
              {selectedClass && (
              <>
                <Label>
                  Is it getting improving or worsening?
                </Label>
                <p className={styles.separator} />
                <div className="flex gap-3 my-2">
                  <OutlineButton
                    key={option}
                    onClick={() => onClickSymptomsStatus(
                      option,
                      ReactionName.Improving,
                    )}
                    isActive={
                            selectedReaction?.value === ReactionName.Improving
                          }
                  >
                    {ReactionName.Improving}
                  </OutlineButton>
                  <OutlineButton
                    onClick={() => onClickSymptomsStatus(
                      option,
                      ReactionName.Worsening,
                    )}
                    isActive={
                            selectedReaction?.value === ReactionName.Worsening
                          }
                  >
                    {ReactionName.Worsening}
                  </OutlineButton>
                  <OutlineButton
                    onClick={() => onClickSymptomsStatus(
                      option,
                      ReactionName.Unchanged,
                    )}
                    isActive={
                            selectedReaction?.value === ReactionName.Unchanged
                          }
                  >
                    {ReactionName.Unchanged}
                  </OutlineButton>
                </div>
                <p className={styles.separator} />
              </>
              )}
            </div>
          );
        })}
      </div>
      <div className="next-button-fixed">
        <Button onClick={() => clickNextOrSubmit(nextPage)}>
          Next
        </Button>
      </div>
    </>
  );
}

export default SymptomsPage;
